import { ElementController } from 'js-common';
import chunk from 'lodash/chunk';
import debounce from 'lodash/debounce';

/**
 * Timeline layout adjustments.
 *
 * Since the content is dynamic and can have any height, just using floats can
 * lead to broken layout. This controller walks through each pair of left and
 * right items and sets margins as needed.
 *
 * @extends {ElementController}
 */
export default class Timeline extends ElementController {
  static id = 'Timeline';

  /**
   * DOM loaded.
   */
  init() {
    this.items = this.getNodes('timeline-item');
    // Is really 'hasEnoughItemsForJSToBeRequired'
    this.hasItems = this.items.length > 2;
  }

  /**
   * Resources loaded.
   */
  load() {
    if (this.hasItems) {
      this.setListState();
      this.setListSpacing(false);
      this.on(window, 'resize', this.handleResize);
    }
  }

  /**
   * Re-check everything after resize.
   */
  handleResize = debounce(() => {
    this.setListState();
    this.setListSpacing(true);
  }, 200);

  /**
   * Calculate necessary 'global' state about the timeline.
   */
  setListState() {
    const firstX = this.items[0].getBoundingClientRect().x;
    const secondX = this.items[1].getBoundingClientRect().x;
    // Should be a single column if the first and second item's left edges are
    // at a similar posiiton. Allow some wiggle room just because.
    const isSingleCol = Math.abs(secondX - firstX) < 50;

    this.setState({
      isSingleCol,
    });
  }

  /**
   * Set the item's spacing depending on state.
   *
   * @param {boolean} clearBefore - If existing margins should be cleared
   *   before new ones are set.
   */
  setListSpacing(clearBefore) {
    if (clearBefore) {
      this.clearMargins();
    }
    if (!this.state.isSingleCol) {
      this.setMargins();
    }
  }

  /**
   * Set margins on items that need it.
   *
   * Checks each pair of blocks and adds top margin to the right side one if
   * it's smaller than the left one.
   */
  setMargins() {
    chunk(this.items, 2).forEach((pairNodes) => {
      // The list can end in a single block
      if (!pairNodes[0] || !pairNodes[1]) {
        return;
      }
      const pair = pairNodes.map((node) => ({
        node,
        height: node.offsetHeight,
      }));
      const heightDiff = pair[0].height - pair[1].height;
      // Don't do anything for tiny differences or if the right block is taller
      // than the left.
      if (heightDiff > 10) {
        // Center the right block
        pair[1].node.style.marginTop = `${heightDiff / 2}px`;
      }
    });
  }

  /**
   * Clear all inline style top margins.
   */
  clearMargins() {
    // Only run on right blocks
    chunk(this.items, 2).forEach((pairNodes) => {
      if (pairNodes[1]) {
        // For some reason removeProperty only takes hyphenated property names
        pairNodes[1].style.removeProperty('margin-top');
      }
    });
  }
}
